import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Mail, Calendar as CalendarIcon, FolderOpen, ExternalLink } from "lucide-react";

const LINKS = [
  {
    key: "mail",
    icon: Mail,
    title: "Apri Casella info@",
    desc: "Vai alla posta di info@operapartners.it su Gmail.",
    href: "https://mail.google.com/mail/u/?authuser=info@operapartners.it",
    testid: "bo-link-mail",
  },
  {
    key: "calendar",
    icon: CalendarIcon,
    title: "Calendario Condiviso",
    desc: "Apri Google Calendar con i calendari condivisi del team.",
    href: "https://calendar.google.com/calendar/u/0/r",
    testid: "bo-link-calendar",
  },
  {
    key: "drive",
    icon: FolderOpen,
    title: "Drive Documenti & Foto",
    desc: "Apri la cartella Google Drive con foto degli appartamenti e documenti.",
    href: "https://drive.google.com/drive/u/0/",
    testid: "bo-link-drive",
  },
];

export default function BackOffice() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C5A059]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      <header className="bg-[#091623] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <LogoFull compact />
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/riservata")}
              data-testid="bo-back-dashboard"
              className="text-sm text-gray-300 hover:text-[#C5A059] flex items-center gap-1"
            >
              <ArrowLeft size={15} /> Dashboard
            </button>
            <Button
              onClick={async () => { await logout(); navigate("/login"); }}
              variant="ghost"
              className="text-gray-300 hover:text-[#C5A059] rounded-none"
            >
              Esci
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
        <span className="label-eyebrow">Back Office · Google Workspace</span>
        <h1 className="font-display font-light text-3xl lg:text-4xl text-white mt-2">
          Strumenti di lavoro
        </h1>
        <p className="text-gray-400 mt-3 max-w-2xl leading-relaxed">
          Accesso rapido a posta, calendario e archivio documenti su Google Workspace del dominio
          operapartners.it.
        </p>

        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              data-testid={l.testid}
              className="group border border-white/10 bg-[#1E2229] p-7 hover:border-[#C5A059]/50 hover:bg-white/[0.03] transition-colors flex flex-col"
            >
              <l.icon className="text-[#C5A059]" size={30} strokeWidth={1.5} />
              <h2 className="font-display text-lg text-white mt-5 flex items-center gap-2">
                {l.title}
                <ExternalLink size={15} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </h2>
              <p className="text-sm text-gray-400 mt-3 leading-relaxed flex-1">{l.desc}</p>
              <span className="text-sm text-[#C5A059] mt-5 group-hover:gap-2 flex items-center gap-1 transition-all">
                Apri <ExternalLink size={14} />
              </span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
